/**
 * `uiStore` — état d'**interface** uniquement (SPEC-UI §1.5). Aucune donnée de jeu ici.
 *
 * Porte trois choses : les réglages persistés, le dialogue maison courant (§7.1 — jamais
 * `window.confirm` / `alert` / `prompt`, règle globale de CLAUDE.md) et la pile de toasts.
 */

import { create } from "zustand";

import type { TonJournal } from "../game/gameClient";

/**
 * ⟦A-8⟧ Ce que l'écran de publication révèle.
 *
 * - `reel` (défaut) : ce que TU as fait — classe et score d'exécution. Ce que le PUBLIC en
 *   fait arrive au fil des jours, et c'est la bibliothèque qui porte le suspense.
 * - `arcade` : la courbe complète tout de suite, mais avec les libellés honnêtes.
 *
 * **Ce n'est pas une difficulté** : les deux modes produisent des résultats strictement
 * identiques, et l'UI doit le dire — sinon « arcade » se lit comme un mode facile.
 */
export type ModeRevelation = "reel" | "arcade";

export interface UISettings {
  /** Tolérance de timing (SPEC-UI §7.3) : fenêtres +30 %, score plafonné à 85. */
  tolerance: boolean;
  /**
   * ⟦A-8⟧ Préférence du JOUEUR (pas de la run) — **persistée côté compte** ⟦M2.40 · A-81⟧ :
   * relue au boot d'une session compte (`adopterPreferencesCompte`) et renvoyée au serveur à
   * chaque changement (`synchroniserRevelation`, `game/client.ts`). Le localStorage reste le
   * cache local et la seule mémoire du bac à sable.
   */
  revealMode: ModeRevelation;
  /**
   * ⟦C8 · A-120⟧ **Le minuteur du manager, opt-in — défaut `false`.** « Le manager tranche les
   * événements après un délai. » Persisté côté compte comme `revealMode` (relu au boot par
   * `adopterPreferencesCompte`, renvoyé par `synchroniserManagerDelai`, `game/client.ts`) ;
   * le localStorage reste le cache local et la seule mémoire du bac à sable. Sans manager en
   * poste OU réglage éteint, un événement bloque exactement comme avant ⟦A-118 §1⟧ : c'est
   * la garantie d'état neutre du chantier.
   */
  managerDelaiActif: boolean;
  /**
   * ⟦A-9⟧ **Le mode découverte** : le jeu se fige et une pop-up explique la mécanique avant
   * chaque mini-jeu joué pour la première fois et à l'apparition de chaque jauge. Actif par
   * défaut — c'est la colonne vertébrale du début de partie, pas une option de confort.
   * Ce qui a déjà été vu se mémorise **par run** (`decouverteStore`) ; ce réglage-ci n'est que
   * l'interrupteur, et il appartient au joueur.
   */
  decouverte: boolean;
  /**
   * ⟦A-129 · C11⟧ **« Tableau de bord complet »** — désactive le HUD progressif
   * (`game/hud.ts`) : toutes les jauges et le panneau des rivaux sont visibles dès le tour 0,
   * comme avant C11. Défaut `false` : c'est le HUD progressif qui est l'état neutre depuis ce
   * chantier, pas l'inverse.
   */
  hudComplet: boolean;
  /**
   * ⟦C21 · A-146⟧ **Repli des fenêtres « Ma chaîne » / « Journal »** (direction F3). « L'état
   * replié/déplié est mémorisé (`localStorage`, comme le HUD progressif) » — même mécanisme que
   * `hudComplet` juste au-dessus : un réglage d'interface, pas un fait de partie, donc pas dans
   * `GameState`. Défaut `false` (dépliées) : l'état neutre est celui de la maquette F3, rien de
   * replié à l'arrivée.
   */
  fenetreChaineRepliee: boolean;
  fenetreJournalRepliee: boolean;
  fenetreActionsRepliee: boolean;
  /**
   * ⟦C62 · lot tableau · A-179⟧ **« Détails » du tableau de bord, ouvert ?** Le tableau limpide (`content/meta.json
   * → tableau_limpide`) range fidélité, hype, momentum, niveau, ratio, surcharge et rivaux derrière un bouton
   * « Détails ». Un réglage d'interface, mémorisé comme le repli des fenêtres au-dessus : un joueur qui l'ouvre
   * le retrouve ouvert. Défaut `false` : fermé, l'état neutre du chantier.
   */
  detailsTableauOuverts: boolean;
  /** Calage audio manuel [−200 ; +200] ms (§7.6). Le Montage v1 est visuel : réservé. */
  audioOffsetMs: number;
  mute: boolean;
  volume: number;
}

const REGLAGES_DEFAUT: UISettings = {
  tolerance: false,
  revealMode: "reel",
  managerDelaiActif: false,
  decouverte: true,
  hudComplet: false,
  fenetreChaineRepliee: false,
  fenetreJournalRepliee: false,
  fenetreActionsRepliee: false,
  detailsTableauOuverts: false,
  audioOffsetMs: 0,
  mute: false,
  volume: 0.8,
};

const CLE_REGLAGES = "maxime.reglages.v1";

function lireReglages(): UISettings {
  try {
    const brut = localStorage.getItem(CLE_REGLAGES);
    if (!brut) return REGLAGES_DEFAUT;
    return { ...REGLAGES_DEFAUT, ...(JSON.parse(brut) as Partial<UISettings>) };
  } catch {
    return REGLAGES_DEFAUT;
  }
}

export interface OptionDialogue {
  id: string;
  label: string;
  hint?: string;
  /** Rend le bouton rouge (choix destructeur ou franchement risqué). */
  danger?: boolean;
}

export type DialogRequest =
  | {
      kind: "confirm";
      title: string;
      body?: string;
      confirmLabel?: string;
      cancelLabel?: string;
      /** `danger` : Échap ne ferme pas, seul un bouton répond (§7.1). */
      danger?: boolean;
      resolve: (ok: boolean) => void;
    }
  | {
      kind: "choose";
      title: string;
      body?: string;
      options: OptionDialogue[];
      annulable?: boolean;
      resolve: (id: string | null) => void;
    }
  | {
      kind: "info";
      title: string;
      body: string;
      confirmLabel?: string;
      icone?: string;
      resolve: (ok: boolean) => void;
    }
  | {
      kind: "prompt";
      title: string;
      label: string;
      body?: string;
      placeholder?: string;
      valeurInitiale?: string;
      /** Rend un message d'erreur, ou `null` si la saisie est acceptable. */
      valider?: (valeur: string) => string | null;
      resolve: (valeur: string | null) => void;
    };

export interface Toast {
  id: number;
  texte: string;
  ton: TonJournal;
  icone: string;
}

interface UIStore {
  settings: UISettings;
  /** Miroir du media query — JAMAIS persisté (SPEC-UI §7.4). */
  reducedMotion: boolean;
  dialog: DialogRequest | null;
  toasts: Toast[];
  /**
   * ⟦A-5⟧ Les annonces de déverrouillage attendent : l'écran de résultat de l'Atelier veut
   * finir son animation de courbe avant qu'un dialogue ne passe par-dessus.
   */
  momentsSuspendus: boolean;
  /**
   * ⟦A-15 §4⟧ L'offre que le joueur a décidé de regarder **maintenant** — l'`event_id` d'une
   * opportunité ouverte, ou `null`.
   *
   * C'est bien de l'état d'INTERFACE et pas de jeu : le moteur garde la fenêtre ouverte tant
   * qu'elle n'a pas expiré, qu'on l'ait regardée ou non (`state.opportunites`). Ce champ ne dit
   * que « la modale est-elle dépliée ? », et il n'est jamais persisté.
   */
  offreOuverte: string | null;
  setSetting<K extends keyof UISettings>(cle: K, valeur: UISettings[K]): void;
  /**
   * ⟦A-8 / M2.40 · A-81⟧ Ce que le COMPTE a mémorisé, appliqué au boot : écrase le cache local
   * sans rien renvoyer au serveur — c'est lui qui vient de parler.
   */
  adopterPreferencesCompte(prefs: { revealMode: ModeRevelation; managerDelaiActif: boolean }): void;
  ouvrirOffre(eventId: string): void;
  fermerOffre(): void;
  setReducedMotion(v: boolean): void;
  suspendreMoments(v: boolean): void;
  fermerDialogue(): void;
  toast(t: Omit<Toast, "id">): void;
  retirerToast(id: number): void;
}

let prochainToast = 1;

export const useUIStore = create<UIStore>((set, get) => ({
  settings: lireReglages(),
  reducedMotion:
    typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches,
  dialog: null,
  toasts: [],
  momentsSuspendus: false,
  offreOuverte: null,

  suspendreMoments(v) {
    set({ momentsSuspendus: v });
  },

  ouvrirOffre(eventId) {
    set({ offreOuverte: eventId });
  },

  fermerOffre() {
    set({ offreOuverte: null });
  },

  setSetting(cle, valeur) {
    const settings = { ...get().settings, [cle]: valeur };
    set({ settings });
    try {
      localStorage.setItem(CLE_REGLAGES, JSON.stringify(settings));
    } catch {
      // Réglages non persistés — la partie reste jouable.
    }
  },

  adopterPreferencesCompte(prefs) {
    get().setSetting("revealMode", prefs.revealMode);
    get().setSetting("managerDelaiActif", prefs.managerDelaiActif);
  },

  setReducedMotion(v) {
    set({ reducedMotion: v });
  },

  fermerDialogue() {
    set({ dialog: null });
  },

  toast(t) {
    const toast: Toast = { ...t, id: prochainToast++ };
    set({ toasts: [...get().toasts, toast] });
    setTimeout(() => get().retirerToast(toast.id), 4200);
  },

  retirerToast(id) {
    set({ toasts: get().toasts.filter((t) => t.id !== id) });
  },
}));

/**
 * L'API à promesses de SPEC-UI §7.1. `await dialog.confirm({…})` — le composant `AppDialog`
 * monté à la racine rend la demande et appelle `resolve`.
 */
export const dialog = {
  confirm(options: Omit<Extract<DialogRequest, { kind: "confirm" }>, "kind" | "resolve">) {
    return new Promise<boolean>((resolve) => {
      useUIStore.setState({ dialog: { kind: "confirm", ...options, resolve } });
    });
  },
  choose(options: Omit<Extract<DialogRequest, { kind: "choose" }>, "kind" | "resolve">) {
    return new Promise<string | null>((resolve) => {
      useUIStore.setState({ dialog: { kind: "choose", ...options, resolve } });
    });
  },
  info(options: Omit<Extract<DialogRequest, { kind: "info" }>, "kind" | "resolve">) {
    return new Promise<boolean>((resolve) => {
      useUIStore.setState({ dialog: { kind: "info", ...options, resolve } });
    });
  },
  /** Saisie de texte — remplace `window.prompt`, qui est interdit (CLAUDE.md, §7.1). */
  prompt(options: Omit<Extract<DialogRequest, { kind: "prompt" }>, "kind" | "resolve">) {
    return new Promise<string | null>((resolve) => {
      useUIStore.setState({ dialog: { kind: "prompt", ...options, resolve } });
    });
  },
};
